import { useContext, useEffect, useState, useSyncExternalStore } from "react";

import { Button } from "@/design-system/Button";
import { Input } from "@/design-system/Input";
import type { I18nContextValue } from "@/translations/implementation/I18nProvider";
import { I18nContext } from "@/translations/implementation/I18nProvider";

const INSPECTOR_TRANSLATION_PREFIX = "component.translation-inspector.";

/** Opens runtime translation tools on demand, including login and quick-reply routes. */
export function TranslationInspector(): React.ReactNode {
  const i18n = useContext(I18nContext);
  const [isOpen, setIsOpen] = useState(false);

  // The browser shortcut stays available while the panel is unmounted.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (
        !event.defaultPrevented &&
        !event.repeat &&
        !event.isComposing &&
        event.ctrlKey &&
        event.shiftKey &&
        !event.metaKey &&
        !event.altKey &&
        (event.code === "Period" || event.key === ".")
      ) {
        event.preventDefault();
        setIsOpen((open) => !open);
      }
    };

    document.addEventListener("keydown", onKeyDown);

    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  return isOpen && i18n ? <TranslationInspectorPanel i18n={i18n} onClose={() => setIsOpen(false)} /> : null;
}

function TranslationInspectorPanel({
  i18n,
  onClose,
}: {
  i18n: I18nContextValue;
  onClose: () => void;
}): React.ReactNode {
  const [query, setQuery] = useState("");
  const lookups = useSyncExternalStore(
    i18n.lookupRegistry.subscribe,
    i18n.lookupRegistry.getSnapshot,
    i18n.lookupRegistry.getSnapshot,
  );
  const observedLookups = lookups.filter((lookup) => !lookup.key.startsWith(INSPECTOR_TRANSLATION_PREFIX));
  const normalized = query.trim().toLowerCase();
  const filteredLookups = observedLookups.filter((lookup) =>
    [lookup.key, lookup.resolvedKey, lookup.template, lookup.value].some((value) =>
      value.toLowerCase().includes(normalized),
    ),
  );

  return (
    <aside
      className="fixed right-space-md bottom-space-md z-overlay flex w-screen max-w-overlay-fluid flex-col gap-space-md rounded-xl border border-border-default bg-background-default p-space-md shadow-overlay sm:max-w-overlay-sm"
      data-translation-inspector
      data-testid="translation-inspector"
      aria-label="Translation inspector"
    >
      <div className="flex items-start justify-between gap-space-md">
        <div>
          <strong className="block text-sm">Translation inspector</strong>
          <span className="text-xs text-text-secondary">
            {observedLookups.length} exact runtime {observedLookups.length === 1 ? "key" : "keys"} observed
          </span>
        </div>
        <span className="rounded-full bg-background-brand-light px-space-sm py-space-xs text-xs text-text-brand">
          {i18n.language.toUpperCase()}
        </span>
      </div>

      <label className="flex flex-col gap-space-xs text-xs text-text-secondary">
        Filter observed keys
        <Input
          type="search"
          width="full"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Key or rendered value"
        />
      </label>
      <div className="max-h-scroll-area-compact overflow-y-auto rounded-lg bg-background-secondary p-space-sm">
        {filteredLookups.length === 0 ? (
          <p className="m-space-xs text-xs text-text-secondary">
            {observedLookups.length === 0
              ? "No translation lookups have rendered yet."
              : "No observed keys match this filter."}
          </p>
        ) : (
          <ul className="flex list-none flex-col gap-space-sm p-0">
            {filteredLookups.map((lookup) => (
              <li className="rounded-lg bg-background-default p-space-sm" key={`${lookup.key}:${lookup.resolvedKey}`}>
                <div className="flex items-start justify-between gap-space-md">
                  <div className="min-w-0">
                    <code className="block overflow-hidden text-xs text-ellipsis">{lookup.key}</code>
                    {lookup.resolvedKey !== lookup.key && (
                      <span className="mt-space-xs block text-xs text-text-secondary">
                        Resolved form: {lookup.resolvedKey}
                      </span>
                    )}
                    <span className="mt-space-xs block text-sm">{lookup.value}</span>
                  </div>
                  <Button
                    data-testid={`translation-inspector-edit-${lookup.key}`}
                    size="sm"
                    type="link"
                    href={createEditorUrl(lookup.key, lookup.language)}
                    target="_blank"
                    isExternal
                  >
                    Edit
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <Button data-testid="translation-inspector-stop" size="sm" type="button" variant="soft" onClick={onClose}>
        Close inspector
      </Button>
    </aside>
  );
}

function createEditorUrl(key: string, locale: string): string {
  const fallbackRoot = "https://translations.areaofpeople.com";
  const configuredRoot =
    typeof window.AOP_TRANSLATIONS_ROOT_URL === "string" ? window.AOP_TRANSLATIONS_ROOT_URL : fallbackRoot;
  let url: URL;

  try {
    url = new URL(configuredRoot || fallbackRoot);
    if (url.protocol !== "https:") {
      url = new URL(fallbackRoot);
    }
  } catch {
    url = new URL(fallbackRoot);
  }

  url.searchParams.set("project", "dashboard");
  url.searchParams.set("locale", locale);
  url.searchParams.set("key", key);

  return url.toString();
}
